import { useEffect, useMemo, useState } from "react";
import {
  addDoc,
  collection,
  doc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { db } from "@/firebase";

interface MailTemplate {
  id: string;
  title: string;
  subject: string;
  body: string;
  attach?: string;
  sign?: string;
  sortNo?: number;
  notificationType?: string;
}

const emptyForm = {
  title: "",
  subject: "",
  body: "",
  attach: "",
  sign: "",
  sortNo: "",
  notificationType: "",
};

export default function MailTemplatePage() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<MailTemplate[]>([]);
  const [selectedId, setSelectedId] = useState<string>("");
  const [form, setForm] = useState({
    title: "",
    subject: "",
    body: "",
    attach: "",
    sign: "",
    sortNo: "",
    notificationType: "",
  });
  const [isVariablePanelOpen, setIsVariablePanelOpen] = useState(false);
  const [selectedCollection, setSelectedCollection] = useState<string>("");
  const [availableFields, setAvailableFields] = useState<string[]>([]);
  const [selectedField, setSelectedField] = useState<string>("");
  const [insertTarget, setInsertTarget] = useState<"subject" | "body" | "sign" | "attach">("body");
  const [fieldLoading, setFieldLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadTemplates = async () => {
    setLoading(true);
    setError(null);
    try {
      const templateQuery = query(collection(db, "mail_templates"), orderBy("sortNo", "asc"));
      const snapshot = await getDocs(templateQuery);
      const data = snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...(docSnap.data() as Omit<MailTemplate, "id">),
      }));
      setTemplates(data);
      if (data.length === 0) {
        setSelectedId("");
        setForm(emptyForm);
      }
    } catch (err) {
      console.error("Failed to load mail templates", err);
      setError("テンプレートの取得に失敗しました。");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTemplates();
  }, []);

  const selectedTemplate = useMemo(
    () => templates.find((template) => template.id === selectedId) || null,
    [templates, selectedId]
  );

  useEffect(() => {
    if (!selectedTemplate) return;
    setForm({
      title: selectedTemplate.title || "",
      subject: selectedTemplate.subject || "",
      body: selectedTemplate.body || "",
      attach: selectedTemplate.attach || "",
      sign: selectedTemplate.sign || "",
      sortNo: selectedTemplate.sortNo?.toString() || "",
      notificationType: selectedTemplate.notificationType || "",
    });
  }, [selectedTemplate]);

  const notificationTypeOptions = [
    { value: "product_shipping", label: "商品発送" },
    { value: "task_return", label: "添削課題返送" },
    { value: "diploma_shipping", label: "認定証発送" },
    { value: "acrylic_shipping", label: "アクリル発送" },
  ];

  const collectionOptions = [
    { value: "Order", label: "Order", templateKey: "order" },
    { value: "User", label: "User", templateKey: "user" },
    { value: "Shikaku", label: "Shikaku", templateKey: "shikaku" },
    { value: "samplesend", label: "samplesend", templateKey: "samplesend" },
  ];
  const extraOrderFields = ["DtrackingNumber", "TtrackingNumber"];
  const extraSampleSendFields = ["name", "produrl", "sample", "storagePath"];

  const selectedCollectionOption =
    collectionOptions.find((option) => option.value === selectedCollection) || null;

  useEffect(() => {
    const loadFields = async () => {
      if (!selectedCollection) {
        setAvailableFields([]);
        setSelectedField("");
        return;
      }
      setFieldLoading(true);
      try {
        const fieldQuery = query(collection(db, selectedCollection), limit(1));
        const snapshot = await getDocs(fieldQuery);
        if (snapshot.empty) {
          if (selectedCollection === "samplesend") {
            const fields = [...extraSampleSendFields].sort();
            setAvailableFields(fields);
            setSelectedField(fields[0] || "");
          } else {
            setAvailableFields([]);
            setSelectedField("");
          }
          return;
        }
        const data = snapshot.docs[0].data() as Record<string, unknown>;
        const baseFields = Object.keys(data);
        const mergedFields =
          selectedCollection === "Order"
            ? Array.from(new Set([...baseFields, ...extraOrderFields]))
            : selectedCollection === "samplesend"
            ? Array.from(new Set([...baseFields, ...extraSampleSendFields]))
            : baseFields;
        const fields = mergedFields.sort();
        setAvailableFields(fields);
        setSelectedField(fields[0] || "");
      } catch (err) {
        console.error("Failed to load template fields", err);
        setAvailableFields([]);
        setSelectedField("");
      } finally {
        setFieldLoading(false);
      }
    };

    loadFields();
  }, [selectedCollection]);

  const handleNew = () => {
    setSelectedId("");
    setForm(emptyForm);
  };

  const handleSave = async () => {
    const title = form.title.trim();
    const subject = form.subject.trim();
    const body = form.body.trim();
    const attach = form.attach.trim();
    const sign = form.sign.trim();
    const notificationType = form.notificationType.trim();
    const sortNoValue = form.sortNo.toString().trim();
    const sortNo = sortNoValue ? Number(sortNoValue) : 0;
    if (!title || !subject || !body) {
      setError("タイトル・件名・本文は必須です。");
      return;
    }
    if (sortNoValue && Number.isNaN(sortNo)) {
      setError("SortNo は数値で入力してください。");
      return;
    }

    setSaving(true);
    setError(null);
    try {
      if (selectedId) {
        await updateDoc(doc(db, "mail_templates", selectedId), {
          title,
          subject,
          body,
          attach: attach || null,
          sign,
          sortNo,
          notificationType: notificationType || null,
          updatedAt: serverTimestamp(),
        });
      } else {
        await addDoc(collection(db, "mail_templates"), {
          title,
          subject,
          body,
          attach: attach || null,
          sign,
          sortNo,
          notificationType: notificationType || null,
          createdAt: serverTimestamp(),
        });
      }
      await loadTemplates();
      if (!selectedId) {
        setForm(emptyForm);
      }
    } catch (err) {
      console.error("Failed to save mail template", err);
      setError("テンプレートの保存に失敗しました。");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="p-6">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50 px-6 py-8">
      <div className="mx-auto max-w-6xl space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <button
              type="button"
              className="text-sm text-blue-600 hover:underline"
              onClick={() => navigate("/admin/templates/documents")}
            >
              ← 書類テンプレート管理へ
            </button>
            <h1 className="mt-2 text-2xl font-bold text-gray-800">メールテンプレート管理</h1>
            <p className="text-sm text-gray-500">
              テンプレート本文には {"{{"}変数{"}}"} を含められます。
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              className="rounded border border-blue-600 px-4 py-2 text-sm font-semibold text-blue-600 shadow-sm hover:bg-blue-50"
              onClick={() => setIsVariablePanelOpen((prev) => !prev)}
            >
              引用変数
            </button>
            <button
              className="rounded bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-blue-700"
              onClick={handleNew}
            >
              新規作成
            </button>
          </div>
        </div>

        {error && <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-600">{error}</div>}

        {isVariablePanelOpen && (
          <div className="rounded-lg border bg-white p-4 text-sm shadow-sm">
            <div className="font-semibold text-gray-700">引用変数</div>
            <div className="mt-3 grid gap-4 md:grid-cols-[200px_1fr_auto]">
              <div className="space-y-1">
                <label className="text-xs font-medium text-gray-500" htmlFor="variable-collection">
                  コレクション
                </label>
                <select
                  id="variable-collection"
                  className="w-full rounded border px-3 py-2"
                  value={selectedCollection}
                  onChange={(e) => setSelectedCollection(e.target.value)}
                >
                  <option value="">選択してください</option>
                  {collectionOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-gray-500" htmlFor="variable-field">
                  フィールド
                </label>
                <select
                  id="variable-field"
                  className="w-full rounded border px-3 py-2"
                  value={selectedField}
                  onChange={(e) => setSelectedField(e.target.value)}
                  disabled={fieldLoading || availableFields.length === 0}
                >
                  {availableFields.length === 0 ? (
                    <option value="">{fieldLoading ? "読み込み中..." : "フィールドがありません"}</option>
                  ) : (
                    availableFields.map((field) => (
                      <option key={field} value={field}>
                        {field}
                      </option>
                    ))
                  )}
                </select>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-gray-500" htmlFor="variable-target">
                  挿入先
                </label>
                <select
                  id="variable-target"
                  className="w-full rounded border px-3 py-2"
                  value={insertTarget}
                  onChange={(e) =>
                    setInsertTarget(e.target.value as "subject" | "body" | "sign" | "attach")
                  }
                >
                  <option value="subject">件名</option>
                  <option value="body">本文</option>
                  <option value="sign">署名</option>
                  <option value="attach">添付ファイル</option>
                </select>
              </div>
              <div className="flex items-end">
                <button
                  type="button"
                  className="rounded bg-emerald-500 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-emerald-600 disabled:opacity-60"
                  disabled={!selectedCollectionOption || !selectedField}
                  onClick={() => {
                    if (!selectedCollectionOption || !selectedField) return;
                    const variableToken = `{{ ${selectedCollectionOption.templateKey}.${selectedField} }}`;
                    setForm((prev) => ({
                      ...prev,
                      [insertTarget]:
                        insertTarget === "body"
                          ? prev.body
                            ? `${prev.body}\n${variableToken}`
                            : variableToken
                          : insertTarget === "subject"
                          ? prev.subject
                            ? `${prev.subject} ${variableToken}`
                            : variableToken
                          : insertTarget === "sign"
                          ? prev.sign
                            ? `${prev.sign}\n${variableToken}`
                            : variableToken
                          : prev.attach
                          ? `${prev.attach}\n${variableToken}`
                          : variableToken,
                    }));
                  }}
                >
                  挿入する
                </button>
              </div>
            </div>
            <div className="mt-2 text-xs text-gray-500">
              例: {"{{"} order.trackingNumbers {"}}"} の形式で本文に挿入されます。
            </div>
          </div>
        )}

        <div className="grid gap-6 lg:grid-cols-[320px_1fr]">
          <div className="rounded-lg border bg-white p-4 shadow-sm">
            <div className="text-sm font-semibold text-gray-700">テンプレート一覧</div>
            <div className="mt-3 space-y-2">
              {templates.map((template) => (
                <button
                  key={template.id}
                  type="button"
                  onClick={() => setSelectedId(template.id)}
                  className={`w-full rounded-lg border px-3 py-2 text-left text-sm transition ${
                    selectedId === template.id
                      ? "border-blue-300 bg-blue-50 text-blue-700"
                      : "border-gray-200 hover:bg-gray-50"
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-semibold">{template.title}</div>
                    <div className="text-xs text-gray-400">SortNo: {template.sortNo ?? 0}</div>
                  </div>
                  <div className="text-xs text-gray-500 truncate">{template.subject}</div>
                </button>
              ))}
              {templates.length === 0 && (
                <div className="text-sm text-gray-500">テンプレートがありません。</div>
              )}
            </div>
          </div>

          <div className="rounded-lg border bg-white p-6 shadow-sm">
            <div className="space-y-4">
              <div className="space-y-1">
                <label className="text-sm font-medium text-gray-700" htmlFor="template-title">
                  タイトル
                </label>
                <input
                  id="template-title"
                  className="w-full rounded-lg border px-3 py-2"
                  value={form.title}
                  onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
                  placeholder="テンプレートのラベル"
                />
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium text-gray-700" htmlFor="template-sort-no">
                  SortNo
                </label>
                <input
                  id="template-sort-no"
                  className="w-full rounded-lg border px-3 py-2"
                  value={form.sortNo}
                  onChange={(e) => setForm((prev) => ({ ...prev, sortNo: e.target.value }))}
                  placeholder="並べ替え順 (数値)"
                />
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium text-gray-700" htmlFor="template-notification-type">
                  通知種別
                </label>
                <select
                  id="template-notification-type"
                  className="w-full rounded-lg border px-3 py-2"
                  value={form.notificationType}
                  onChange={(e) => setForm((prev) => ({ ...prev, notificationType: e.target.value }))}
                >
                  <option value="">選択してください</option>
                  {notificationTypeOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium text-gray-700" htmlFor="template-subject">
                  件名
                </label>
                <input
                  id="template-subject"
                  className="w-full rounded-lg border px-3 py-2"
                  value={form.subject}
                  onChange={(e) => setForm((prev) => ({ ...prev, subject: e.target.value }))}
                  placeholder="メール件名"
                />
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium text-gray-700" htmlFor="template-body">
                  本文
                </label>
                <textarea
                  id="template-body"
                  className="min-h-[220px] w-full rounded-lg border px-3 py-2"
                  value={form.body}
                  onChange={(e) => setForm((prev) => ({ ...prev, body: e.target.value }))}
                  placeholder={"例: {{customerName}} 様\n\nお問い合わせありがとうございます。"}
                />
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium text-gray-700" htmlFor="template-attach">
                  添付ファイル (storagePath)
                </label>
                <textarea
                  id="template-attach"
                  className="min-h-[90px] w-full rounded-lg border px-3 py-2"
                  value={form.attach}
                  onChange={(e) => setForm((prev) => ({ ...prev, attach: e.target.value }))}
                  placeholder={"例: {{ samplesend.storagePath }}"}
                />
                <p className="text-xs text-gray-500">
                  storagePath を改行区切りで指定できます。
                </p>
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium text-gray-700" htmlFor="template-sign">
                  署名
                </label>
                <textarea
                  id="template-sign"
                  className="min-h-[120px] w-full rounded-lg border px-3 py-2"
                  value={form.sign}
                  onChange={(e) => setForm((prev) => ({ ...prev, sign: e.target.value }))}
                  placeholder={"例: \n株式会社◯◯\n担当: {{staffName}}"}
                />
              </div>
            </div>
            <div className="mt-6 flex justify-end">
              <button
                className="rounded bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-blue-700 disabled:opacity-60"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? "保存中..." : selectedId ? "更新する" : "作成する"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
